import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getData, navigateV2, useHistoryStore } from '../utils/router';
import UserContext, { PusherContext, UserProfileContext, WarehouseContext } from '../utils/getContext';
import { checkIsPartner, getUserRoles, userInfo } from '../utils/memo';
import { logOutFunction } from '../utils/utils';
import { usePusher } from '../utils/pusher.utills';
import AgreeModal from '../../widgets/agreeModal/AgreeModal';
import CustomPasswordModal from '../../widgets/customPasswordModal/CustomPasswordModal';
import PaymasterModal from '../../widgets/paymasterModal/PaymasterModal';

const ProtectedOffice = ({ children, onlyForRoles = [], confirmPresence }) => {
    const { userToken, lang, storageLoaded } = useContext(UserContext);
    const [profileData, setProfileData] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [roles, setRoles] = useState([]);
    const [agreeModal, showAgreeModal] = useState(false);

    const [warehouse, setWarehouse] = useState(null);
    const [warehouseState, setWarehouseState] = useState({});
    const { paymasterModal } = warehouseState;

    const { pusherClient } = usePusher(loaded && userToken, lang);

    const updateProfileData = useCallback(
        (cache) => {
            if (!cache) {
                checkIsPartner.cache.clear();
            }
            checkIsPartner(userToken, lang).then(async (data) => {
                if (data?.data?.data) {
                    setProfileData(data.data.data);
                    setLoaded(true);
                    await userInfo(data.data.data, userToken, lang, setProfileData, true).then((profileWithRank) => {
                        setProfileData(profileWithRank);
                    });
                } else {
                    if (data === 401) {
                        logOutFunction();
                    } else {
                        setLoaded(true);
                    }
                }
            });
        },
        [lang, userToken]
    );

    useEffect(() => {
        if (storageLoaded) {
            if (!userToken) {
                navigateV2('/');
            } else {
                (async () => {
                    const { data, status } = (await getUserRoles(userToken, lang)) || {};
                    if (status === 401) {
                        logOutFunction();
                    } else {
                        setRoles(data || []);
                        updateProfileData(true);
                        if (onlyForRoles && onlyForRoles?.length) {
                            const hasRole = data?.some((role) => onlyForRoles.includes(role.name));
                            if (!hasRole) {
                                navigateV2('/error/403');
                            }
                        }
                    }
                })();
            }
        }
    }, [lang, updateProfileData, storageLoaded, userToken, onlyForRoles]);

    // Документи користувача для підтвердження
    useEffect(() => {
        if (!userToken || confirmPresence || !profileData || !storageLoaded) return;
        getData('/api/v1/documents/for-signature', userToken, lang).then((res) => {
            if (res?.data?.length) {
                showAgreeModal({
                    documents: {
                        ...res,
                        data: res.data.map((item) => ({
                            ...item,
                            file: {
                                ...item?.file,
                                link: {
                                    url: item?.file?.link,
                                    httpHeaders: {
                                        Authorization: `Bearer ${userToken}`
                                    }
                                }
                            }
                        }))
                    }
                });
            } else {
                showAgreeModal(false);
            }
        });
    }, [userToken, lang, confirmPresence, profileData, storageLoaded]);

    useEffect(() => {
        if (!userToken) return;
        getData('/api/v1/warehouse/warehouses', userToken, lang).then((res) => {
            if (res?.data) {
                setWarehouse(res);
            }
        });
    }, [userToken, lang]);

    useHistoryStore(confirmPresence || loaded, 'office', confirmPresence);

    if (!userToken) return null;
    return (
        <PusherContext.Provider value={{ pusherClient }}>
            <UserProfileContext.Provider
                value={{
                    roles,
                    loaded,
                    profileData,
                    updateProfileData,
                    officePage: true
                }}
            >
                <WarehouseContext.Provider
                    value={{
                        warehouse,
                        setWarehouse,
                        setState: (key, value) => setWarehouseState((s) => ({ ...s, [key]: value }))
                    }}
                >
                    {children}
                    {agreeModal ? <AgreeModal documents={agreeModal.documents} /> : null}
                    {paymasterModal ? <PaymasterModal paymasterModal={paymasterModal} /> : null}
                </WarehouseContext.Provider>
                <CustomPasswordModal />
            </UserProfileContext.Provider>
        </PusherContext.Provider>
    );
};

export default ProtectedOffice;
